"use client";

import { useCallback } from "react";
import { validateFields } from "@/lib/utils";
import { FormState, Section, VariantConfig, CategoryItem } from "../types";
import { SECTION_TYPES } from "../constants";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
  buildDefaultFieldValues,
  buildSavePayload,
  extractCategoriesFromResponse,
  getAdminApiFailureMessage,
  isAdminApiEnvelopeSuccess,
  unwrapAdminApiPayload,
  prepareSectionPreviewData,
} from "../utils";

interface UseCategoryActionsProps {
  selectedSection: Section;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  pageId?: string;
  pageComponentId?: string;
  variantConfig: VariantConfig | null;
  categoryFieldsConfig: any[];
  currentUserId?: string | number;
  onSave: (section: Section) => void;
  fetchData: (forceFetch?: boolean) => Promise<void>;
  /** When set, creates the parent record (e.g. faq) on demand if missing */
  ensureComponentParent?: () => Promise<number | null>;
}

// ============================================================================
// Parent ID Field Mapping by Section Type
// ============================================================================
const PARENT_ID_CONFIG: Record<
  string,
  { fieldName: string; errorMessage: string }
> = {
  [SECTION_TYPES.FAQ]: {
    fieldName: "faq_id",
    errorMessage: "Unable to find FAQ. Please ensure the FAQ component is created first.",
  },
  [SECTION_TYPES.VISIONARIES]: {
    fieldName: "visionaries_id",
    errorMessage:
      "Unable to find Visionaries. Please ensure the Visionaries component is created first.",
  },
  [SECTION_TYPES.FOOTER]: {
    fieldName: "footer_id",
    errorMessage: "Unable to find Footer. Please ensure the Footer component is created first.",
  },
};

const getParentIdField = (
  sectionType: string,
  parentIdValue: number | null,
  pageComponentId?: string
): { fields: Record<string, any>; error: string | null } => {
  const config = PARENT_ID_CONFIG[sectionType];

  if (config) {
    return parentIdValue
      ? { fields: { [config.fieldName]: parentIdValue }, error: null }
      : { fields: {}, error: config.errorMessage };
  }

  return { fields: { component_id: Number(pageComponentId) }, error: null };
};

// ============================================================================
// Form State Reset Helper
// ============================================================================
const createFormResetState = (
  defaults: Record<string, any>
): Partial<FormState> => ({
  formFields: defaults,
  selectedItemType: null,
  selectedCategoryId: null,
  editingItemId: null,
  formFieldErrors: {},
  formFieldMessages: {},
  categoryMembershipIds: [],
});

// ============================================================================
// Main Hook
// ============================================================================
export const useCategoryActions = ({
  selectedSection,
  formState,
  setFormState,
  pageId,
  pageComponentId,
  variantConfig,
  categoryFieldsConfig,
  currentUserId,
  onSave,
  fetchData,
  ensureComponentParent,
}: UseCategoryActionsProps) => {
  const getMethod = variantConfig?.getMethod;
  const createCategoryMethod = (variantConfig as any)?.createCategoryMethod;
  const updateCategoryMethod = (variantConfig as any)?.updateCategoryMethod;
  const deleteCategoryMethod = (variantConfig as any)?.deleteCategoryMethod;
  const fetchCategoriesMethod = (variantConfig as any)?.fetchCategoriesMethod;
  const fetchNestedItemsMethod = (variantConfig as any)?.fetchNestedItemsMethod;
  const fetchItemsMethod = (variantConfig as any)?.fetchItemsMethod;
  const childItemsFieldName = (variantConfig as any)?.childItemsFieldName || "categories";
  const supportsOptionalCategories =
    (variantConfig as any)?.supportsOptionalCategoriesToggle === true;
  const useCategoriesOff =
    formState.componentFields?.use_categories === false ||
    formState.componentFields?.use_categories === 0;

  // Three-level structure check
  const isThreeLevelStructure = Boolean(fetchNestedItemsMethod);

  // ---------------------------------------------------------------------------
  // Helper: Fetch Parent ID
  // ---------------------------------------------------------------------------
  const fetchParentId = async (): Promise<number | null> => {
    if (formState.parentId) return formState.parentId;
    if (!pageComponentId) return null;

    if (ensureComponentParent) {
      const ensured = await ensureComponentParent();
      if (ensured != null) return ensured;
    }

    if (!getMethod) return null;

    const componentId = Number(pageComponentId);
    if (isNaN(componentId) || componentId <= 0) return null;

    try {
      const response = await getMethod(componentId);
      const raw = unwrapAdminApiPayload(response);
      const data: Record<string, any> =
        raw != null && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, any>) : {};
      const parentId = data.id || null;
      parentId && setFormState((prev) => ({ ...prev, parentId }));
      return parentId;
    } catch (error) {
      console.error("Failed to get parent ID:", error);
      return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Helper: Fetch category with nested items (for three-level structures)
  // ---------------------------------------------------------------------------
  const fetchCategoryWithNestedItems = async (category: any) => {
    if (!fetchNestedItemsMethod || !fetchItemsMethod) return category;
    
    try {
      const nestedResponse = await fetchNestedItemsMethod(category.id);
      const semesters = nestedResponse?.data || nestedResponse || [];
      
      const semestersWithItems = await Promise.all(
        (Array.isArray(semesters) ? semesters : []).map(async (semester: any) => {
          try {
            const itemResponse = await fetchItemsMethod(semester.id);
            const items = itemResponse?.data || itemResponse || [];
            return { ...semester, subjects: Array.isArray(items) ? items : [], items: Array.isArray(items) ? items : [] };
          } catch {
            return { ...semester, subjects: [], items: [] };
          }
        })
      );
      
      return { ...category, semesters: semestersWithItems };
    } catch {
      return { ...category, semesters: [] };
    }
  };

  // ---------------------------------------------------------------------------
  // Helper: Update Preview
  // ---------------------------------------------------------------------------
  const updatePreview = async (parentIdValue: number | null) => {
    if (!onSave || !fetchCategoriesMethod || !pageComponentId) return;

    try {
      const response = await fetchCategoriesMethod(Number(pageComponentId));
      const { categories: categoriesList, componentData } = extractCategoriesFromResponse(
        response,
        childItemsFieldName
      );

      // For three-level structures, fetch nested items for each category
      let fullCategories = categoriesList;
      if (isThreeLevelStructure && fetchNestedItemsMethod && fetchItemsMethod) {
        fullCategories = await Promise.all(
          categoriesList.map((cat: any) => fetchCategoryWithNestedItems(cat))
        );
      }

      const mergedData = { ...componentData, ...formState.componentFields };
      const id = parentIdValue || componentData.id || selectedSection.componentId || 0;

      onSave(
        prepareSectionPreviewData(selectedSection, id, { ...mergedData, id }, fullCategories, childItemsFieldName)
      );
    } catch (error) {
      console.error("Failed to fetch updated data for preview:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // Category Click Handler
  // ---------------------------------------------------------------------------
  const handleCategoryClick = useCallback(
    (category: CategoryItem) => {
      if (category.id === -1 || category.is_uncategorized_bucket) return;
      const categoryFields = categoryFieldsConfig.reduce(
        (acc, field) => ({
          ...acc,
          [field.fieldName]: category[field.fieldName as keyof CategoryItem] ?? field.defaultValue ?? "",
        }),
        {}
      );

      const hybrid = (variantConfig as VariantConfig | null)?.visionariesHybridForm === true;
      const isDefCat =
        (category as any).is_default === true ||
        (category as any).is_default === 1 ||
        (category as any).is_default === "1";
      const membershipFromCategory = hybrid
        ? isDefCat
          ? []
          : (category.items || []).map((i: any) => Number(i.id)).filter((n: number) => !Number.isNaN(n))
        : undefined;

      setFormState((prev) => ({
        ...prev,
        selectedItemType: "category",
        selectedCategoryId: category.id,
        selectedItemId: null,
        editingItemId: String(category.id),
        formFields: categoryFields,
        currentFieldsConfig: categoryFieldsConfig,
        formFieldErrors: {},
        formFieldMessages: {},
        ...(membershipFromCategory !== undefined
          ? { categoryMembershipIds: membershipFromCategory }
          : {}),
      }));
    },
    [categoryFieldsConfig, setFormState, variantConfig]
  );

  // ---------------------------------------------------------------------------
  // New Category Handler
  // ---------------------------------------------------------------------------
  const handleNewCategory = useCallback(async () => {
    if (ensureComponentParent) {
      const id = await ensureComponentParent();
      if (id == null) return;
    }
    const defaults = buildDefaultFieldValues(categoryFieldsConfig);

    setFormState((prev) => ({
      ...prev,
      selectedItemType: "category",
      selectedCategoryId: null,
      selectedNestedId: null,
      selectedItemId: null,
      editingItemId: null,
      formFields: { ...defaults },
      currentFieldsConfig: [...categoryFieldsConfig],
      formFieldErrors: {},
      formFieldMessages: {},
      categoryMembershipIds: [],
    }));
  }, [categoryFieldsConfig, setFormState, ensureComponentParent]);

  // ---------------------------------------------------------------------------
  // Category Save Handler
  // ---------------------------------------------------------------------------
  const handleCategorySave = useCallback(async () => {
    if (!pageComponentId) {
      showErrorToast("Page component ID is required");
      return;
    }

    if (
      supportsOptionalCategories &&
      useCategoriesOff &&
      !(variantConfig as VariantConfig | null)?.visionariesHybridForm
    ) {
      showErrorToast(
        'Turn on "Use categories" in component settings before adding or editing categories.'
      );
      return;
    }

    const { errors, messages } = validateFields(categoryFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));

    if (Object.keys(errors).length > 0) {
      showValidationErrorToast();
      return;
    }

    try {
      const isUpdate = formState.editingItemId && !String(formState.editingItemId).startsWith("temp_");
      const parentIdValue = await fetchParentId();

      // Get parent ID field based on section type
      const { fields: parentIdFields, error } = getParentIdField(
        selectedSection.type,
        parentIdValue,
        pageComponentId
      );

      if (error) {
        showErrorToast(error);
        return;
      }

      const hybrid = (variantConfig as VariantConfig | null)?.visionariesHybridForm === true;
      const isDefForm =
        formState.formFields?.is_default === true ||
        formState.formFields?.is_default === 1 ||
        formState.formFields?.is_default === "1";
      const membershipPayload =
        hybrid && selectedSection.type === SECTION_TYPES.VISIONARIES && !isDefForm
          ? { profile_ids: formState.categoryMembershipIds ?? [] }
          : {};

      const payload = buildSavePayload(
        formState.formFields,
        pageId,
        currentUserId,
        Boolean(isUpdate),
        { ...parentIdFields, ...membershipPayload }
      );

      // Save operation using object mapping
      const saveOperations = {
        update: {
          canExecute: () => isUpdate && updateCategoryMethod && formState.editingItemId,
          execute: async () => {
            const response = await updateCategoryMethod!(Number(formState.editingItemId), payload);
            if (!isAdminApiEnvelopeSuccess(response)) {
              showErrorToast(
                getAdminApiFailureMessage(
                  response,
                  "Failed to update category. Save the Visionaries component first if this section is new."
                )
              );
              return;
            }
            showSuccessToast("Category updated successfully");
          },
        },
        create: {
          canExecute: () => !isUpdate && createCategoryMethod,
          execute: async () => {
            const response = await createCategoryMethod!(payload);
            if (!isAdminApiEnvelopeSuccess(response)) {
              showErrorToast(
                getAdminApiFailureMessage(
                  response,
                  "Failed to create category. Save the Visionaries component first if this section is new."
                )
              );
              return;
            }
            const row = response.data as { id?: number } | undefined;
            const responseId = row?.id;
            responseId &&
              setFormState((prev) => ({
                ...prev,
                editingItemId: String(responseId),
                selectedCategoryId: responseId,
              }));
            showSuccessToast("Category created successfully");
          },
        },
      };

      const operation = Object.values(saveOperations).find((op) => op.canExecute());
      if (!operation) {
        showErrorToast("Could not save category. Ensure the form is in a valid state and try again.");
        return;
      }
      await operation.execute();

      // Reset form and refresh
      const defaults = buildDefaultFieldValues(categoryFieldsConfig);
      setFormState((prev) => ({ ...prev, ...createFormResetState(defaults) }));
      await updatePreview(parentIdValue);
      await fetchData(true);
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to save category");
    }
  }, [
    pageComponentId,
    pageId,
    categoryFieldsConfig,
    formState.formFields,
    formState.categoryMembershipIds,
    formState.editingItemId,
    formState.parentId,
    formState.componentFields,
    selectedSection,
    currentUserId,
    getMethod,
    createCategoryMethod,
    updateCategoryMethod,
    fetchCategoriesMethod,
    onSave,
    fetchData,
    setFormState,
    supportsOptionalCategories,
    useCategoriesOff,
    variantConfig,
  ]);

  // ---------------------------------------------------------------------------
  // Category Delete Handler
  // Note: Confirmation should be handled by caller using DeleteDialog
  // ---------------------------------------------------------------------------
  const handleCategoryDelete = useCallback(
    async (category: CategoryItem) => {
      if (category.id === -1 || category.is_uncategorized_bucket) return;
      if (!deleteCategoryMethod) return;

      try {
        await deleteCategoryMethod(category.id);
        showSuccessToast("Category deleted successfully");

        // Clear form if deleting selected category
        formState.selectedCategoryId === category.id &&
          setFormState((prev) => ({
            ...prev,
            selectedItemType: null,
            selectedCategoryId: null,
            editingItemId: null,
            formFields: {},
          }));

        await updatePreview(formState.parentId);
        await fetchData(true);
      } catch (error) {
        showErrorToast("Failed to delete category");
      }
    },
    [deleteCategoryMethod, formState.selectedCategoryId, formState.parentId, fetchData, setFormState]
  );

  // ---------------------------------------------------------------------------
  // Toggle Expand Handler
  // ---------------------------------------------------------------------------
  const toggleCategoryExpand = useCallback(
    (categoryId: number) => {
      setFormState((prev) => {
        const newSet = new Set(prev.expandedCategoryIds);
        newSet.has(categoryId) ? newSet.delete(categoryId) : newSet.add(categoryId);
        return { ...prev, expandedCategoryIds: newSet };
      });
    },
    [setFormState]
  );

  return {
    handleCategoryClick,
    handleNewCategory,
    handleCategorySave,
    handleCategoryDelete,
    toggleCategoryExpand,
  };
};
